/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { lockForStatus } from './subscription-lock';
import type { AgencyStatus } from '@/features/onboarding/types/agency-dashboard';

/** Asks the overview to open the regularisation modal on arrival. */
export const REGULARIZE_PARAM = 'regularize';

/**
 * Where a card for an inactive agency leads.
 *
 * Each state has exactly one screen that explains it, and this is the only
 * place that decides which: the workspace card and the shell used to disagree
 * about the cancelled state, sending one to the plan step and the other to a
 * payment step for a subscription that no longer exists.
 *
 * @param agencyId - The agency the card belongs to.
 * @param status - The agency's effective subscription status.
 * @returns The path to navigate to.
 */
export function inactiveAgencyDestination(agencyId: string, status?: AgencyStatus | null): string {
  const base = `/app/agencies/${agencyId}`;
  const lock = lockForStatus(status);

  /* No subscription, one thing to do about it: the plans. */
  if (lock === 'draft') return `${base}/subscribe?step=plan`;
  /* C1 — reactivation lives where the overview would be. */
  if (lock === 'cancelled') return `${base}/dashboard`;
  /*
   * B3 — a suspended agency still has a subscription, so it is not sent to
   * buy another one. It lands on its own overview with the regularisation
   * modal already open, which is where the card is replaced.
   */
  if (lock === 'read-only') return `${base}/dashboard?${REGULARIZE_PARAM}=1`;

  return base;
}
